import { useMemo } from "react";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { EntityLoadingState } from "../../components/ui/EntityPageChrome.jsx";
import { compactPrimaryButton } from "../../components/ui/buttonStyles.js";
import {
  PageErrorBanner,
  TypeCatalogCard,
  TypeCatalogListFooter,
  TypeCatalogSectionHeader,
  TypeCatalogToolbar,
} from "../../components/ui/TypeCatalogToolbar.jsx";
import { TypeCatalogEmptyState } from "../../components/emptyStates/TypeCatalogEmptyState.jsx";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { useAccountServiceAccounts } from "../../logic/hooks/useAccountServiceAccounts.js";
import { buildAccountDetailTokens } from "./accountDetailTheme.js";
import { ServiceAccountKeysModal } from "./components/ServiceAccountKeysModal.jsx";
import { ServiceAccountList } from "./components/ServiceAccountList.jsx";
import { ServiceAccountModal } from "./components/ServiceAccountModal.jsx";

function KeyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21 2-2 2" />
      <path d="m7.5 16.5 9 18" />
      <path d="M3 21v-4l10.5-10.5a2.12 2.12 0 0 1 3 3L6 20z" />
      <path d="m14 6 4 4" />
    </svg>
  );
}

export function AccountServiceAccountsTab({ account }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const vm = useAccountServiceAccounts(account?.id);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  const filtered = vm.search.trim().length > 0;
  const showing = vm.items.length;
  const countLabel = filtered ? `${showing} of ${vm.total} accounts` : `${vm.total} account${vm.total === 1 ? "" : "s"}`;

  const handleDelete = (row) => {
    requestDelete({
      title: "Delete service account",
      description: "This revokes API access immediately. Integrations using these keys will stop working.",
      confirmText: row.name,
      entityLabel: "Service account",
      confirmLabel: "Delete service account",
      onConfirm: () => vm.removeServiceAccount(row.id),
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <TypeCatalogSectionHeader
        title="Service accounts"
        subtitle={`API credentials for ${account?.companyName ?? "this account"}. Each account has a public key for identification and a private key for authentication.`}
        action={
          <button type="button" onClick={vm.openCreate} style={compactPrimaryButton(A)}>
            {Ico.plus}
            Create service account
          </button>
        }
        c={c}
      />

      <PageErrorBanner message={vm.error} />

      <TypeCatalogCard ap={ap} dark={dark}>
        <TypeCatalogToolbar search={vm.search} onSearchChange={vm.setSearch} placeholder="Search by name or public key…" countLabel={countLabel} c={c} />

        {vm.loading ? (
          <EntityLoadingState />
        ) : vm.total === 0 ? (
          <TypeCatalogEmptyState
            c={c}
            A={A}
            icon={<KeyIcon />}
            message="No service accounts yet"
            hint="Create a service account to generate public and private API keys for integrations."
            onAction={vm.openCreate}
            actionLabel="Create service account"
          />
        ) : showing === 0 ? (
          <TypeCatalogEmptyState c={c} A={A} icon={<KeyIcon />} message="No matching service accounts" hint="Try a different search term." />
        ) : (
          <ServiceAccountList items={vm.items} ap={ap} saving={vm.saving} onEdit={vm.openEdit} onDelete={handleDelete} onRotate={vm.openRotateConfirm} />
        )}

        {!vm.loading && showing > 0 && (
          <TypeCatalogListFooter c={c}>
            Showing {showing} service account{showing === 1 ? "" : "s"}
            {filtered ? ` matching “${vm.search.trim()}”` : ""}
          </TypeCatalogListFooter>
        )}
      </TypeCatalogCard>

      <ServiceAccountModal open={vm.modalOpen} onClose={vm.closeModal} onSave={vm.saveServiceAccount} editing={vm.editing} saving={vm.saving} />
      <ServiceAccountKeysModal
        open={Boolean(vm.keysReveal)}
        title={vm.keysReveal?.title}
        subtitle={vm.keysReveal?.subtitle}
        publicKey={vm.keysReveal?.publicKey}
        privateKey={vm.keysReveal?.privateKey}
        onClose={vm.closeKeysReveal}
      />
      <DeleteConfirmModal
        open={Boolean(vm.rotateTarget)}
        title="Rotate API keys"
        description={`Rotating keys for "${vm.rotateTarget?.name}" will invalidate the current private key. Update any integrations with the new keys.`}
        confirmText={vm.rotateTarget?.name ?? ""}
        entityLabel="Service account"
        confirmLabel={vm.saving ? "Rotating…" : "Rotate keys"}
        busy={vm.saving}
        onCancel={vm.closeRotateConfirm}
        onConfirm={vm.confirmRotateKeys}
      />
      <DeleteConfirmModal {...deleteModalProps} />
    </div>
  );
}
